/**
 * DAC Cart Upsell Split Test
 *
 * When two or more cart_upsells metaobject entries tie on `priority`, cart-upsells.liquid
 * renders all of them as "arms". This picks exactly one arm per visitor and removes the rest.
 *
 * Assignment is a random float persisted in localStorage, so a shopper sees the same arm for
 * the life of that browser - the cart drawer re-renders on every add/remove/qty change, and a
 * per-render pick would flip the offer mid-session and make conversions unattributable.
 *
 * A float (rather than a stored index) means adding a third arm later re-splits everyone
 * evenly across three instead of stranding existing assignments.
 */
(function () {
  const BUCKET_KEY = 'dac_upsell_bucket';
  const SHOWN_ARM_KEY = 'dac_upsell_arm_shown';

  /* Stable per-visitor value in [0,1). Falls back to a fresh random if storage is blocked
     (private mode, cookie walls) - that visitor just isn't sticky. */
  function getBucket() {
    try {
      let value = parseFloat(window.localStorage.getItem(BUCKET_KEY));
      if (!(value >= 0 && value < 1)) {
        value = Math.random();
        window.localStorage.setItem(BUCKET_KEY, String(value));
      }
      return value;
    } catch (e) {
      return Math.random();
    }
  }

  function track(eventName, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, payload));
  }

  /* Remember the arm this shopper was last shown, for GTM to read as a variable.
     Deliberately NOT written to the cart or the line item: anything stored on the order flows
     through the Orders API to ShipStation and slows warehouse processing. Exposure is tracked
     in analytics only. */
  function setShownArm(armId) {
    try {
      window.localStorage.setItem(SHOWN_ARM_KEY, armId);
    } catch (e) {
      /* storage blocked - the dataLayer impression event still fires */
    }
  }

  function getShownArm() {
    try {
      return window.localStorage.getItem(SHOWN_ARM_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  /* Price of the offer, as a decimal. Mapped to GA4's standard `value` parameter in GTM, which is
     what makes revenue sum per arm without registering a custom metric. */
  function armValue(el) {
    const parsed = parseFloat(el.getAttribute('data-upsell-value'));
    return isNaN(parsed) ? 0 : parsed;
  }

  function resolve(root) {
    const scope = root || document;
    const group = scope.querySelector('[data-upsell-ab-group]');
    if (!group || group.hasAttribute('data-upsell-ab-resolved')) return;

    /* Direct children only. The add button inside each card also carries data-upsell-arm,
       so a descendant query would count every arm twice and strip the buttons. */
    const arms = Array.prototype.slice.call(group.children).filter(function (el) {
      return el.hasAttribute('data-upsell-arm');
    });
    /* Nothing left to show. Liquid already committed the drawer to the cart-upsell branch, so
       there is no fallback to fall back to - hide the box rather than leave a bare heading. */
    if (!arms.length) {
      const container = group.closest('.product-upsell');
      if (container) container.classList.add('hidden');
      return;
    }

    const chosen = arms.length === 1 ? arms[0] : arms[Math.floor(getBucket() * arms.length)];
    arms.forEach(function (arm) {
      if (arm !== chosen) arm.remove();
    });

    chosen.classList.add('is-active');
    group.setAttribute('data-upsell-ab-resolved', '');

    /* Stamp the count before the early return below, so the add handler can tell a genuine test
       exposure from a lone leftover offer. */
    chosen.setAttribute('data-upsell-arm-count', String(arms.length));

  }

  /* Reporting is deliberately split from resolve(). Resolving has to happen at page load so no
     losing arm is ever painted, but the drawer is rendered into every page and hidden - firing the
     impression there would count shoppers who never opened the cart and had no chance to add.
     This runs when the drawer is actually on screen, so an impression means the arm was seen. */
  function reportImpression(root) {
    const scope = root || document;
    const group = scope.querySelector('[data-upsell-ab-group]');
    if (!group || group.hasAttribute('data-upsell-impression-sent')) return;

    const chosen = Array.prototype.slice.call(group.children).filter(function (el) {
      return el.hasAttribute('data-upsell-arm');
    })[0];
    if (!chosen) return;

    /* A single eligible entry is not a test. This happens routinely mid-session: once a shopper
       adds one arm, its SKU is in the cart, Liquid filters it out, and the other renders alone.
       That exposure follows a purchase decision, so it stays out of both sides of the funnel. */
    const armCount = parseInt(chosen.getAttribute('data-upsell-arm-count'), 10) || 0;
    if (armCount < 2) return;

    group.setAttribute('data-upsell-impression-sent', '');

    const armId = chosen.getAttribute('data-upsell-arm');
    track('dac_upsell_impression', {
      upsell_arm: armId,
      upsell_arm_count: armCount,
      upsell_variant_id: chosen.getAttribute('data-upsell-variant-id'),
      upsell_product_title: chosen.getAttribute('data-upsell-product-title'),
      upsell_value: armValue(chosen),
      upsell_currency: chosen.getAttribute('data-upsell-currency') || '',
    });
    setShownArm(armId);
  }

  window.DACUpsellTest = {
    resolve: resolve,
    reportImpression: reportImpression,
    track: track,
    getShownArm: getShownArm,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      resolve();
    });
  } else {
    resolve();
  }
})();
